import { useEffect, useRef, useState } from "react";

type GoogleCredentialResponse = { credential?: string };
type GoogleAccounts = {
  id: {
    initialize: (options: { client_id: string; callback: (response: GoogleCredentialResponse) => void; auto_select?: boolean; ux_mode?: "popup" | "redirect" }) => void;
    renderButton: (parent: HTMLElement, options: { type: "standard"; theme: "outline"; size: "large"; shape: "rectangular"; text: "continue_with"; width: number }) => void;
  };
};

declare global {
  interface Window { google?: { accounts: GoogleAccounts } }
}

let googleScriptPromise: Promise<GoogleAccounts> | null = null;

const loadGoogleAccounts = (attempt = 0): Promise<GoogleAccounts> => {
  if (window.google?.accounts) return Promise.resolve(window.google.accounts);
  if (googleScriptPromise) return googleScriptPromise;
  googleScriptPromise = new Promise<GoogleAccounts>((resolve, reject) => {
    const existing = document.getElementById("google-identity-services") as HTMLScriptElement | null;
    const script = existing ?? document.createElement("script");
    const loaded = () => window.google?.accounts ? resolve(window.google.accounts) : reject(new Error("Google sign-in did not load."));
    const failed = () => {
      script.remove();
      googleScriptPromise = null;
      if (attempt < 1) {
        window.setTimeout(() => resolve(loadGoogleAccounts(attempt + 1)), 750);
      } else {
        reject(new Error("Google sign-in is unavailable. Continue with email."));
      }
    };
    script.addEventListener("load", loaded, { once: true });
    script.addEventListener("error", failed, { once: true });
    if (!existing) {
      script.id = "google-identity-services";
      script.src = "https://accounts.google.com/gsi/client";
      script.referrerPolicy = "strict-origin-when-cross-origin";
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }).catch((error) => {
    googleScriptPromise = null;
    throw error;
  });
  return googleScriptPromise;
};

export function GoogleSignInButton({ clientId, busy, onCredential, onError }: {
  clientId: string;
  busy: boolean;
  onCredential: (credential: string) => void;
  onError: (message: string) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef(onCredential);
  const errorRef = useRef(onError);
  const [ready, setReady] = useState(false);
  callbackRef.current = onCredential;
  errorRef.current = onError;

  useEffect(() => {
    let active = true;
    void loadGoogleAccounts().then((accounts) => {
      if (!active || !containerRef.current) return;
      accounts.id.initialize({
        client_id: clientId,
        auto_select: false,
        ux_mode: "popup",
        callback: (response) => {
          window.focus();
          if (response.credential) callbackRef.current(response.credential);
          else errorRef.current("Google did not return a sign-in credential. Continue with email.");
        }
      });
      containerRef.current.replaceChildren();
      accounts.id.renderButton(containerRef.current, {
        type: "standard",
        theme: "outline",
        size: "large",
        shape: "rectangular",
        text: "continue_with",
        width: Math.max(240, Math.floor(containerRef.current.getBoundingClientRect().width))
      });
      setReady(true);
    }).catch((error) => {
      if (active) errorRef.current(error instanceof Error ? error.message : "Google sign-in is unavailable. Continue with email.");
    });
    return () => { active = false; };
  }, [clientId]);

  return <div className={`google-auth${busy ? " busy" : ""}`} aria-busy={!ready || busy}>
    <div ref={containerRef} className="google-button-host" />
    {!ready && <span>Loading Google sign-in…</span>}
  </div>;
}
